import React from 'react';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton } from 'formik-antd';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { Col, Row, Space } from 'antd';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import { DatePicker, IntlMessages } from '@/components';
import dayjs from '@/lib/dayjsExtended';
import { DATE_FORMAT } from '../../../../const/system';
import { mkValidationSchema } from './FormFilterDictionary.const';



const FormFilterDictionary = ({
  onSubmit,
  onReset,
  filterOptions,
  initialValues,
  isSubmitting,
  formErrors = {},
  showSourceFilter = true,
}) => {
  const intl = useIntl();
  const [ searchQueryParams ] = useSearchParams();
  const multipleSources = searchQueryParams.get('multipleSources') ?? null;

  const filterSourceOptions = filterOptions.sources.map((source) => {
    return {
      label: intl.formatMessage({ id: `structures-source-${source.value}` }),
      value: source.value,
    };
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={mkValidationSchema(multipleSources)}
      isSubmitting={isSubmitting}
      initialValues={initialValues}
      initialErrors={formErrors}
      onSubmit={onSubmit}
      onReset={onReset}
    >
      {({ isValid, dirty }) => {
        return (
          <Form layout="vertical" className='w-full'>
            <Row gutter={16}>
              <Col span={24}>
                <FormItem
                  className='ant-form-item-col'
                  name="name"
                  label={<IntlMessages id="ui-general-name" />}
                >
                  <Input
                    name='name'
                    placeholder={intl.formatMessage({ id: 'ui-general-name' })}
                  />
                </FormItem>
              </Col>
              {showSourceFilter && (
                <Col span={24}>
                  <FormItem
                    className='ant-form-item-col'
                    name="source"
                    label={<IntlMessages id="ui-dictionaries-source-field" />}
                  >
                    <Select
                      name="source"
                      mode="multiple"
                      maxTagCount="responsive"
                      allowClear
                      showSearch
                      options={filterSourceOptions}
                      placeholder={<IntlMessages id="form-dictionaries-source-placeholder" />}
                    />
                  </FormItem>
                </Col>
              )}
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="field"
                  label={<IntlMessages id="form-dictionaries-field-field" />}
                >
                  <Select
                    defaultValue={initialValues.field}
                    allowClear
                    name="field"
                    options={filterOptions.fields}
                    placeholder={<IntlMessages id="form-dictionaries-field-placeholder" />}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="field_value"
                  label={<IntlMessages id="form-dictionaries-field-value-field" />}
                >
                  <Input
                    name='field_value'
                    placeholder={intl.formatMessage({ id: 'form-dictionaries-field-value-field' })}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <FormItem
                  className='ant-form-item-col'
                  name="version"
                  label={<IntlMessages id="form-dictionaries-structure-field" />}
                >
                  <Select
                    allowClear
                    name="version"
                    options={filterOptions.versions}
                    placeholder={<IntlMessages id="form-dictionaries-structure-placeholder" />}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="created_from"
                  label={<IntlMessages id="ui-general-created-from" />}
                >
                  <DatePicker
                    name='created_from'
                    format={DATE_FORMAT}
                    fromFormikValue={(value) => (value ? dayjs(value) : null)}
                    toFormikValue={(dateString) => dateString}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="created_to"
                  label={<IntlMessages id="ui-general-created-to" />}
                >
                  <DatePicker
                    name='created_to'
                    fromFormikValue={(value) => (value ? dayjs(value) : null)}
                    toFormikValue={(dateString) => (dateString ? `${dateString}T23:59:59` : dateString)}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <Space>
                  <ResetButton disabled={false} loading={isSubmitting}>
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                  <SubmitButton
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id='ui-general-apply' />
                  </SubmitButton>
                </Space>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormFilterDictionary.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    field: PropTypes.string,
    created_from: PropTypes.string,
    created_to: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    fields: PropTypes.array.isRequired,
    sources: PropTypes.array.isRequired,
    versions: PropTypes.array.isRequired,
  }).isRequired,
  formErrors: PropTypes.object,
  isSubmitting: PropTypes.bool.isRequired,
  showSourceFilter: PropTypes.bool,
};

export default FormFilterDictionary;
